import { Link } from "react-router-dom";
import { CreateAccount1AlertProps, CreateAccount1Fields } from "../../components/SmartErrorAlert";
import { hasInvalidField } from "../getApiErrorMessage";
import translate from "../translate";
import { ErrorContentGenerator, ErrorContentMap, InvalidField, InvalidFieldMessages } from "./getAlertContent";
import { UnlockAccount1ErrState } from "./unlockAccount1Errors";

type CreateAccount1ErrState = UnlockAccount1ErrState | 'alreadyExists';

export const createAccount1ApiErrorContent: ErrorContentMap<CreateAccount1ErrState> = {
    notFound: {
        header: translate('not_able_to_find_in_our_records'),
        body: translate('check_information_try_again'),
        analyticsErr: "We're not able to find you in our records. Check the information you entered and try again."
    },
    alreadyExists: {
        header: translate('looks_like_already_have_account'),
        body: <>
            <Link to={'/forgot-username-and-password'}>{translate('get_help')}</Link> {translate('do_not_remember_your_username_and_password')}
        </>,
        analyticsErr: "It looks like you already have an account. Get help if you don't remember your username or password."
    },
    invalidBedapInfo: {
        header: translate('cant_process_request'),
        body: <>
            {translate('for_help_call_1_800_medicare')} ({<a href="tel:1-800-633-4227">1-800-633-4227</a>}){' '}
            {translate('ask_rep_to_create_account')}{' '}
            {translate('tty_users_can_call')} {<a href="tel:1-877-486-2048">1-877-486-2048</a>}.
        </>,
        analyticsErr: "We can't process your request. For help, call 1-800-MEDICARE (1-800-633-4227) and ask the representative to create an account for you. TTY users can call 1-877-486-2048."
    },
    unknown: {
        header: translate('we_cant_process_request'),
        body: translate('we_are_sorry_for_inconvenience_try_creating_account_later'),
        analyticsErr: "We can't process your request at this time. We're sorry for any inconvenience. Try creating your account later."
    }
}

export const createAccount1InvalidFieldMessages: InvalidFieldMessages<CreateAccount1Fields> = {
    mbi: {
        linkText: 'medicare_number',
        messages: {
            empty: 'enter_your_medicare_number',
            invalid: 'check_mbi'
        }
    },
    coverageStartDate: {
        linkText: 'effective_date',
        messages: {
            empty: 'enter_date_your_coverage_started',
            invalid: 'check_medicare_cover_start_date'
        }
    }
}

export const createAccountStep1ErrorFns = new ErrorContentGenerator<CreateAccount1AlertProps, CreateAccount1Fields, CreateAccount1ErrState>({
    apiErrorContent: createAccount1ApiErrorContent,
    invalidFieldMessages: createAccount1InvalidFieldMessages,
    invalidFieldHeader: 'complete_items_to_create_account',
    getRefMap: props => {
        return {
            mbi: props.mbiRef,
            coverageStartDate: props.coverageStartDateRef
        }
    },
    getErrState: apiResponse => {
        if (!apiResponse) {
            return null;
        }

        switch (apiResponse.status) {
            case 400:
                const { errors } = apiResponse.data;
                const notFound = errors?.some(x => x.message.includes('did not match'));
                if (notFound) {
                    return 'notFound';
                }
                const invalidFields = createAccountStep1ErrorFns.getApiInvalidFields(apiResponse);
                if (invalidFields.length) {
                    return null;
                }
                return 'unknown';
            case 401:
                return 'invalidBedapInfo';
            case 404:
                return 'notFound';
            case 409:
                return 'alreadyExists';
            default:
                return 'unknown';
        }
    },
    getApiInvalidFields: apiResponse => {
        if (!apiResponse || apiResponse.status !== 400) {
            return [];
        }

        const invalidFields: InvalidField<CreateAccount1Fields>[] = [];
        const { errors } = apiResponse.data;
        const invalidMbi = hasInvalidField(errors, 'mbi');
        if (invalidMbi) {
            invalidFields.push({
                name: 'mbi',
                type: 'invalid'
            });
        }
        const invalidDate = hasInvalidField(errors, 'effectiveDatePart') || hasInvalidField(errors, 'effectiveDate');
        if (invalidDate) {
            invalidFields.push({
                name: 'coverageStartDate',
                type: 'invalid'
            });
        }
        return invalidFields;
    },
    isIntermediateStep: true
});